import { observer } from 'mobx-react';
import React, { useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import modifiersStore from '../../store/modifiersStore';
import { IModifier } from '../../types/modifiers';
import { SCREENS } from 'src/constants/screens';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { history } from 'src/services/history';
import { TBooleanInNumber } from 'src/types/main';
import { confirmDelete } from 'src/utils/modalConfirmations';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import DeleteRestoreBlock from 'src/view/components/DeleteRestoreBlock/DeleteRestoreBlock';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import Text, { TEXT } from 'src/view/components/Text/Text';
import './style.scss';

interface IProps {
  modifier?: IModifier;
}

const ModifiersListRow: React.FC<IProps> = observer((props: IProps) => {
  const switcherFrozen = useMemo(() => {
    return Boolean(props.modifier?.is_delete);
  }, [props.modifier]);

  const editModifier = () => {
    if (!props.modifier) return;
    history.push(SCREENS.MODIFIERS_EDIT(props.modifier.id));
  };

  const deleteModifier = () => {
    confirmDelete({
      message: localeStore.t('confirm_deleting_modifier'),
      onConfirm: () => props.modifier && modifiersStore.deleteModifier(props.modifier.id),
    });
  };

  const restoreModifier = () => {
    if (!props.modifier || modifiersStore.isLoading) return;
    modifiersStore.restoreModifier(props.modifier.id);
  };

  const changeStatus = useCallback(
    (status: TBooleanInNumber) => {
      if (!props.modifier || switcherFrozen) return;
      modifiersStore.changeStatus(props.modifier.id, status);
    },
    [props.modifier, switcherFrozen]
  );

  const renderAvailabilitySwitcher = useCallback((): JSX.Element | null => {
    if (!props.modifier) return <Skeleton width={50} height={30} style={{ borderRadius: 30 }} />;

    if (props.modifier.is_delete) return null;

    return <Switcher status={props.modifier.status} disabled={switcherFrozen || modifiersStore.isLoading} onChange={changeStatus} />;
  }, [props.modifier, changeStatus, switcherFrozen, modifiersStore.isLoading]);

  const {
    modifierGlobalAvailability: availabilityRestrictionAccess,
    modifierDelete: deleteRestrictionAccess,
    modifierRestore: restoreRestrictionAccess,
  } = integrationsStore.posRestrictions || {};

  return (
    <li className="modifiers-list-row d-flex full-width">
      <div
        onClick={() => (props.modifier?.is_delete ? undefined : editModifier())}
        className={`d-flex flex-row align-items-center w-70 ${props.modifier?.is_delete ? '' : 'cursor-pointer'}`}
      >
        {props.modifier ? <Text type={TEXT.LIST_ROW} text={props.modifier.name} /> : <Skeleton width={250} height={19.2} />}
      </div>
      <div className={`d-flex flex-row align-items-center w-30 justify-content-${props.modifier?.is_delete ? 'end' : 'between'}`}>
        <IntegrationManager setting={availabilityRestrictionAccess} withTooltip>
          {renderAvailabilitySwitcher()}
        </IntegrationManager>
        <IntegrationManager
          setting={props.modifier?.is_delete ? restoreRestrictionAccess : deleteRestrictionAccess}
          tooltipOptions={{ minWidth: 355 }}
          withTooltip
        >
          <DeleteRestoreBlock
            skeleton={!props.modifier}
            disabled={modifiersStore.isLoading}
            isDelete={props.modifier?.is_delete}
            onRestore={restoreModifier}
            onDelete={deleteModifier}
          />
        </IntegrationManager>
      </div>
    </li>
  );
});

export default ModifiersListRow;
